import { parseBlob } from "music-metadata-browser"
import type {
  ExtractedMetadata,
  ImportedRow,
  Playlist,
  Song,
} from "./metadataTypes"

export const inputClass =
  "w-full min-w-0 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white outline-none transition placeholder:text-white/20 focus:border-white/30 focus:bg-black/40"

export const buttonClass =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.1] active:scale-[.98] disabled:pointer-events-none disabled:opacity-30"

export const formatDuration = (seconds?: number | null) => {
  if (!Number.isFinite(seconds)) return ""
  const total = Math.max(0, Math.floor(seconds!))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

export const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`
}

export const slugify = (value: string) =>
  value
    .replace(/\.[^/.]+$/, "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

export const songImagePath = (
  playlistLink: string,
  artist: string,
  title: string
) => {
  const name = slugify(`${artist} ${title}`)
  return playlistLink && name
    ? `../image/listas/${playlistLink}/${name}.jpg`
    : ""
}

export const getFileParts = (fileName: string) => {
  const name = fileName.replace(/\.[^/.]+$/, "").trim()
  const i = name.indexOf(" - ")

  if (i <= 0) return { artist: "", title: name }

  const artist = name.slice(0, i).trim()
  const title = name.slice(i + 3).trim()

  return {
    artist: /[^\d\s]/.test(artist) ? artist : "",
    title: title || name,
  }
}

export const normalizeSong = (
  song: Partial<Song>,
  index: number
): Song => ({
  id: song.id || crypto.randomUUID(),
  songName: song.songName || "",
  songimg: song.songimg || "",
  songArtist: song.songArtist || "",
  artistLink: song.artistLink || "",
  link: song.link || "",
  genre: song.genre || "",
  trackTime: song.trackTime || "",
  index,
  genres: [],
})

export const normalizePlaylist = (value: unknown): Playlist => {
  const s = value as Record<string, any>

  const songs = Array.isArray(s.playlistData)
    ? s.playlistData
    : []

  return {
    id: String(s.id || ""),
    type: String(s.type || "playlist"),
    featured: Boolean(s.featured),
    title: String(s.title || ""),
    link: String(s.link || ""),
    playlistBg: String(s.playlistBg || "#111111"),
    imgUrl: String(s.imgUrl || ""),
    hoverColor: String(s.hoverColor || s.playlistBg || "#111111"),
    artist: String(s.artist || ""),
    artistImgUrl: String(s.artistImgUrl || ""),
    artistBio: String(s.artistBio || ""),
    artistDownload: String(s.artistDownload || ""),
    bannerUno: String(s.bannerUno || ""),
    downloadData: Array.isArray(s.downloadData) ? s.downloadData : [],
    playlistImages: Array.isArray(s.playlistImages)
      ? s.playlistImages.map(String)
      : [],
    playlistData: songs.map((song: Partial<Song>, i: number) =>
      normalizeSong(song, i)
    ),
  }
}

export async function extractMetadata(file: File): Promise<ExtractedMetadata> {
  const meta = await parseBlob(file)
  const picture = meta.common.picture?.[0]

  let imageUrl: string | null = null

  if (picture?.data) {
    imageUrl = URL.createObjectURL(
      new Blob([new Uint8Array(picture.data)], {
        type: picture.format || "image/jpeg",
      })
    )
  }

  const duration =
    typeof meta.format.duration === "number"
      ? meta.format.duration
      : null

  return {
    file,
    fileName: file.name,
    title: meta.common.title || "",
    artist: meta.common.artist || "",
    album: meta.common.album || "",
    year: meta.common.year ? String(meta.common.year) : "",
    genre: meta.common.genre?.join(", ") || "",
    duration,
    trackTime: formatDuration(duration),
    imageUrl,
    mimeType: file.type || "audio/mpeg",
    size: file.size,
    bitrate: meta.format.bitrate ?? null,
    sampleRate: meta.format.sampleRate ?? null,
    channels: meta.format.numberOfChannels ?? null,
  }
}

export const createImportedRow = async (
  file: File,
  playlistLink: string
): Promise<ImportedRow> => {
  try {
    const metadata = await extractMetadata(file)
    const fileData = getFileParts(file.name)

    let artist = fileData.artist || metadata.artist.trim()
    let title = metadata.title.trim() || fileData.title

    if (
      title.includes(" - ") &&
      artist &&
      title.split(" - ")[0].trim().toLowerCase() === artist.toLowerCase()
    ) {
      title = title.split(" - ").slice(1).join(" - ").trim()
    }

    return {
      id: crypto.randomUUID(),
      fileName: file.name,
      title,
      artist,
      album: metadata.album,
      year: metadata.year,
      genre: metadata.genre,
      trackTime: metadata.trackTime,
      songimg: songImagePath(playlistLink, artist, title),
      link: "",
      imageUrl: metadata.imageUrl,
      selected: true,
      metadata,
    }
  } catch {
    const { artist, title } = getFileParts(file.name)

    const metadata: ExtractedMetadata = {
      file,
      fileName: file.name,
      title,
      artist,
      album: "",
      year: "",
      genre: "",
      duration: null,
      trackTime: "",
      imageUrl: null,
      mimeType: file.type || "audio/mpeg",
      size: file.size,
      bitrate: null,
      sampleRate: null,
      channels: null,
    }

    return {
      id: crypto.randomUUID(),
      fileName: file.name,
      title,
      artist,
      album: "",
      year: "",
      genre: "",
      trackTime: "",
      songimg: songImagePath(playlistLink, artist, title),
      link: "",
      imageUrl: null,
      selected: true,
      metadata,
    }
  }
}

export const importedToSong = (
  row: ImportedRow,
  index: number,
  playlistLink: string
): Song => {
  const file = getFileParts(row.fileName)

  const artist = file.artist || row.artist.trim()
  const title = file.artist ? file.title : row.title.trim() || file.title

  return {
    id: String(index + 1),
    songName: title,
    songimg: songImagePath(playlistLink, artist, title),
    songArtist: /[^\d\s]/.test(artist) ? artist : "",
    artistLink: "",
    link: row.link || "",
    genre: row.genre || "",
    trackTime: row.trackTime || "",
    index,
    genres: [],
  }
}

export const emptySong = (index: number): Song => ({
  id: crypto.randomUUID(),
  songName: "",
  songimg: "",
  songArtist: "",
  artistLink: "",
  link: "",
  genre: "",
  trackTime: "",
  index,
  genres: [],
})

const escapeString = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r?\n/g, "\\n")

export const serialize = (
  value: unknown,
  indent = 0
): string => {
  const space = " ".repeat(indent)
  const next = " ".repeat(indent + 2)

  if (typeof value === "string")
    return `"${escapeString(value)}"`

  if (
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value)
  }

  if (value == null) return "null"

  if (Array.isArray(value)) {
    return value.length
      ? `[\n${value
          .map(v => `${next}${serialize(v, indent + 2)}`)
          .join(",\n")}\n${space}]`
      : "[]"
  }

  if (typeof value === "object") {
    const entries = Object.entries(value)

    return entries.length
      ? `{\n${entries
          .map(
            ([key, val]) =>
              `${next}${key}: ${serialize(val, indent + 2)}`
          )
          .join(",\n")}\n${space}}`
      : "{}"
  }

  return "undefined"
}

export const downloadText = (
  content: string,
  filename: string
) => {
  const url = URL.createObjectURL(
    new Blob([content], {
      type: "text/plain;charset=utf-8",
    })
  )

  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()

  setTimeout(() => URL.revokeObjectURL(url), 500)
}